import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  resetSideLoadedDataSetUploadError,
  uploadSideLoadedDataSet,
} from "actions/sideLoadedData";

import { ColumnMappingGrid } from "components/Business/SideLoadedBusinesses/ColumnMappingGrid";
import { Button, LinkButton, SecondaryButton } from "components/Form";
import ErrorMessage from "components/ErrorMessage";
import { Dropzone } from "components/Form/FormikControls/FileInput";
import { Label } from "components/Form/FormikControls";
import { StyledInput } from "components/Form/FormikControls/layout";
import { Column, Row } from "components/Layout";
import LoadingSpinner from "components/LoadingSpinner";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import { FullWidthSelect } from "components/SearchableSelector/ReactSelect";
import { SmallText, Text } from "components/Text";

import { SIDE_LOADED_PREVIEW_ROW_COUNT } from "constants/sideLoadedBusinesses";

import { parseCsv } from "lib/parseCsv";

import { downloadSideLoadedDataSetTemplate } from "lib/sideLoadedDataSetTemplate";
import {
  entriesForTemplate,
  hasDoubledUpTargets,
  mappedColumnsSummary,
  mappingEntriesMatch,
  slotOverflow,
} from "lib/sideLoadedFieldMapping";

import {
  columnSignature,
  getIsUploadingSideLoadedDataSet,
  getSideLoadedDataSetUploadError,
} from "selectors";

import { useSideLoadedFieldConfigurations } from "../useSideLoadedFieldConfigurations";
import { TemplateEditWarning } from "./TemplateEditWarning";
import { NEW_TEMPLATE, useTemplateOptions } from "./useTemplateOptions";

// react-dropzone v10 takes MIME types/extensions, not the v14 object form. The
// extension matters: browsers report CSVs as text/csv, application/vnd.ms-excel
// or nothing at all depending on the platform.
const ACCEPTED_CSV_TYPES = ["text/csv", "application/csv", ".csv"];

interface SideLoadedDataSetsModalProps {
  onClose: () => void;
}

export function SideLoadedDataSetsModal(
  props: SideLoadedDataSetsModalProps,
): React.JSX.Element {
  const { onClose } = props;
  const dispatch = useDispatch();
  const isUploading = useSelector(getIsUploadingSideLoadedDataSet);
  const serverError = useSelector(getSideLoadedDataSetUploadError);

  const [fileTypeError, setFileTypeError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [sourceColumns, setSourceColumns] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [entries, setEntries] = useState<SideLoadedDataMappingEntry[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] =
    useState<string>(NEW_TEMPLATE);

  const { userRoleContentTypeName, configurations } =
    useSideLoadedFieldConfigurations();
  const {
    templates,
    templateOptions,
    templateIdBySignature,
    dataSetCountByTemplateId,
  } = useTemplateOptions(sourceColumns);

  // A stored template can carry more Notes or PIC columns than there are slots
  // even though the grid will not add one, and the extras are dropped in silence.
  const overflow = useMemo(
    () => slotOverflow(entries, configurations),
    [entries, configurations],
  );

  const doubledUpTargets = useMemo(
    () => hasDoubledUpTargets(entries, configurations),
    [entries, configurations],
  );

  const mappingError = overflow
    ? `At most ${overflow.slotCount} columns can be mapped to ${overflow.label} - unmap ${overflow.overflow} to upload.`
    : doubledUpTargets
      ? "Each AgriNous field can only be mapped once."
      : null;

  const uploadError = fileTypeError || serverError || mappingError;

  // The last failure's message lives in the store, so clear it on open rather
  // than presenting it as though this attempt had failed.
  useEffect(() => {
    dispatch(resetSideLoadedDataSetUploadError());
  }, [dispatch]);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: unknown[]) => {
      if (rejectedFiles.length > 0) {
        setFileTypeError("Please select a CSV file.");
        return;
      }
      const csvFile = acceptedFiles[0];
      if (!csvFile) {
        return;
      }
      setFileTypeError(null);

      // Parsed here so the columns can be mapped against their own values
      // before anything is sent. The server parses it again on import.
      csvFile
        .text()
        .then(text => {
          const parsed = parseCsv<Record<string, string>>(text, {
            header: true,
            skipEmptyLines: true,
            preview: SIDE_LOADED_PREVIEW_ROW_COUNT,
          });
          const columns = parsed.meta.fields || [];
          if (!columns.length || !parsed.data.length) {
            setFileTypeError("That CSV has no columns or no data rows.");
            return;
          }

          const matchedId = templateIdBySignature[columnSignature(columns)];
          const matched = templates.find(template => template.id === matchedId);

          // A second pass because the first stops at the preview limit.
          let rowCount = 0;
          parseCsv(text, {
            header: true,
            skipEmptyLines: true,
            step: () => {
              rowCount += 1;
            },
          });

          const fileName = csvFile.name.replace(/\.csv$/i, "");
          setFile(csvFile);
          setName(fileName);
          setTemplateName(fileName);
          setSourceColumns(columns);
          setRows(parsed.data);
          setTotalRowCount(rowCount);
          setSelectedTemplateId(matched ? matched.id : NEW_TEMPLATE);
          setEntries(entriesForTemplate(matched, columns, configurations));
        })
        .catch(() => setFileTypeError("That file could not be read."));
    },
    [templateIdBySignature, templates, configurations],
  );

  const selectedTemplate = templates.find(
    template => template.id === selectedTemplateId,
  );
  const hasEdits =
    Boolean(selectedTemplate) &&
    !mappingEntriesMatch(selectedTemplate.entries, entries, sourceColumns);

  const selectedOption =
    templateOptions.find(option => option.value === selectedTemplateId) ||
    templateOptions[0];

  const onTemplateChange = useCallback(
    (option: { value: string }) => {
      const chosen = templates.find(template => template.id === option.value);
      setSelectedTemplateId(option.value);
      setTemplateName(chosen ? chosen.name : name);
      setEntries(entriesForTemplate(chosen, sourceColumns, configurations));
    },
    [templates, sourceColumns, configurations, name],
  );

  const onUpload = useCallback(() => {
    if (!file || !name.trim() || mappingError) {
      return;
    }
    dispatch(
      uploadSideLoadedDataSet({
        name: name.trim(),
        file,
        sourceColumns,
        entries,
        templateId: selectedTemplateId || null,
        templateName: templateName.trim() || name.trim(),
        hasTemplateEdits: hasEdits,
      }),
    );
  }, [
    dispatch,
    file,
    name,
    templateName,
    sourceColumns,
    entries,
    selectedTemplateId,
    hasEdits,
    mappingError,
  ]);

  const mappedCount = entries.length;

  return (
    <ZoomyDialog open onClose={onClose} fullWidth fullScreen={Boolean(file)}>
      <DialogTitle onClose={onClose}>
        {file ? `Map the columns in ${file.name}` : "Upload CSV"}
      </DialogTitle>

      <DialogContent dividers>
        <Column fullWidth fullHeight={Boolean(file)}>
          {!file && (
            <Row paddingVertical={2}>
              <Text fontSize="alpha">
                Upload a CSV of your businesses. Column headers can use any
                names - they are mapped to AgriNous fields in the next step.{" "}
                <LinkButton
                  type="button"
                  onClick={downloadSideLoadedDataSetTemplate}
                >
                  Download the template
                </LinkButton>{" "}
                to start from.
              </Text>
            </Row>
          )}

          {uploadError && (
            <Row paddingVertical={2}>
              <ErrorMessage>{uploadError}</ErrorMessage>
            </Row>
          )}

          {isUploading && (
            <Row justifyCenter paddingVertical={3}>
              <LoadingSpinner size={24} actionName="Uploading" subjectName="" />
            </Row>
          )}

          {!isUploading && !file && (
            // Dropzone has no default for disabled, so it has to be passed.
            <Dropzone
              onDrop={onDrop}
              allowedFileTypes={ACCEPTED_CSV_TYPES}
              disabled={false}
            />
          )}

          {!isUploading && file && (
            <Column gridGap={3} fullWidth flexGrow>
              <Column gridGap={1}>
                <Label tooltip="Reuses a saved column mapping, or creates a new one from your choices below.">
                  Column Template
                </Label>
                <FullWidthSelect
                  value={selectedOption}
                  onChange={onTemplateChange}
                  options={templateOptions}
                  menuPortalTarget={document.body}
                />
              </Column>

              {selectedTemplate
                ? hasEdits && (
                    <TemplateEditWarning
                      templateName={selectedTemplate.name}
                      otherDataSetCount={
                        dataSetCountByTemplateId[selectedTemplate.id] || 0
                      }
                    />
                  )
                : entries.length > 0 && (
                    <Column gridGap={1}>
                      <Label tooltip="Saves this column mapping so it can be applied automatically the next time you upload a file with the same headers.">
                        Template Name
                      </Label>
                      <StyledInput
                        data-tour="sideLoadedUploadTemplateName"
                        value={templateName}
                        onChange={event => setTemplateName(event.target.value)}
                        placeholder="Name this column mapping"
                      />
                    </Column>
                  )}

              <ColumnMappingGrid
                sourceColumns={sourceColumns}
                rows={rows}
                totalRowCount={totalRowCount}
                entries={entries}
                onChange={setEntries}
                userRoleContentTypeName={userRoleContentTypeName}
              />

              <Column gridGap={1}>
                <SmallText>
                  {mappedColumnsSummary(mappedCount, sourceColumns.length)}
                </SmallText>
              </Column>

              <Column gridGap={1}>
                <Label tooltip="Identifies which records came from this upload.">
                  Data Set Name
                </Label>
                <StyledInput
                  data-tour="sideLoadedDataSetName"
                  value={name}
                  onChange={event => setName(event.target.value)}
                  placeholder="Name this data set"
                />
              </Column>
            </Column>
          )}
        </Column>
      </DialogContent>

      <DialogActions>
        <SecondaryButton
          type="button"
          data-tour="closeSideLoadedDataSetUpload"
          onClick={onClose}
        >
          Close
        </SecondaryButton>
        {file && !isUploading && (
          <Button
            type="button"
            data-tour="confirmSideLoadedDataSetUpload"
            onClick={onUpload}
            disabled={!name.trim() || Boolean(mappingError)}
          >
            Upload
          </Button>
        )}
      </DialogActions>
    </ZoomyDialog>
  );
}
